/*
 * @Author: ken yang
 * @Date: 2022-09-07 18:07:16
 * @LastEditTime: 2023-08-30 18:25:22
 * @LastEditors: ken yang
 * @Description:
 * @FilePath: /uexch_pc/src/utils/request/index.js
 */
import axios from 'axios'
import useFixTime from './fixTime'
import useDelNullKey from './delNullKey'
import DiaWarn from '@/components/dialog/DiaWarn.js'
import { attachRequestMonitor, reportRequestFailure, reportRequestSuccess } from '@/utils/monitor'

const baseURL = serveUrl
const instance = axios.create({
  baseURL: baseURL,
  timeout: 1000 * 60 * 5,
})

const okCode = 1
const warnCode = -1
const needLoginCode = 50
const upgradeCode = 700

const noTipsCode = [18040, 18111]

const isNeedLoginRequest = (url) => needLoginUrl.includes(url)

const markAsNonReportable = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return payload
  }

  if (payload instanceof Error) {
    payload.__reportable = false
    return payload
  }

  return {
    ...payload,
    __reportable: false,
  }
}

useFixTime(instance)
useDelNullKey(instance)

// 请求发出时除了原有签名逻辑外，还会记录开始时间，供监控层计算耗时。
instance.interceptors.request.use(async (config) => {
  const userStore = useUserStore()
  const configStore = useConfigStore()
  const time = +new Date()

  attachRequestMonitor(config)

  if (config.auth !== false && isNeedLoginRequest(config.url) && isEmpty(userStore.token)) {
    return Promise.reject({
      code: needLoginCode,
      config,
      __needLoginBlocked: true,
    })
  }

  config.headers.time = time
  config.headers.deviceType = 3
  const obj = fixTime(delNullKey(config.data || {}), '/')
  config.headers.signature = encrypt.requestSign(obj, time)
  config.headers.language = configStore.locale

  if (config.auth === false) {
    delete config.headers.token
  } else {
    if (userStore.token) {
      config.headers.token = userStore.token
    } else {
      delete config.headers.token
    }
  }
  return config
})

// 普通错误提示
const errorDia = (data, config = {}) => {
  const silentErrorCodes = Array.isArray(config.silentErrorCodes) ? config.silentErrorCodes : []
  const silentErrorMessages = Array.isArray(config.silentErrorMessages) ? config.silentErrorMessages : []
  if (
    noTipsCode.includes(data.code) ||
    silentErrorCodes.includes(data.code) ||
    silentErrorMessages.includes(data.message)
  ) return

  ElMessage.error(config.errorMessageMap?.[data.message] || data.message)
}

// 登出错误防抖
let canAlertLogoutError = true
const alertLogoutError = (message, cooldownMs = 3000) => {
  if (!canAlertLogoutError) return
  ElMessage.error(message)
  canAlertLogoutError = false
  setTimeout(() => {
    canAlertLogoutError = true
  }, cooldownMs)
}

// 登出错误提示（防重复）
const logoutErrorDia = (data) => {
  if (!noTipsCode.includes(data.code)) {
    alertLogoutError(data.message)
  }
}

instance.interceptors.response.use(
  (res) => {
    const userStore = useUserStore()
    const { config, data } = res
    const okCodes = Array.isArray(config.okCodes) ? config.okCodes : [okCode]

    // 请求成功后先交给 request monitor 判断是否属于慢请求。
    reportRequestSuccess(res)

    if (data.code == needLoginCode) {
      logoutErrorDia(data)
      userStore.logout()
      return Promise.reject(markAsNonReportable(data))
    } else if (data.code === upgradeCode) {
      router.push('/upgrade')
      return Promise.reject(markAsNonReportable(data))
    } else if (data.code === warnCode) {
      DiaWarn({ data: data.message })
    } else if (!okCodes.includes(data.code)) {
      errorDia(data, config)
      return Promise.reject(markAsNonReportable(data))
    }
    return res
  },
  async (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }
    if (error?.__needLoginBlocked) {
      return Promise.reject({
        code: needLoginCode,
        message: '该接口需要登录后才能访问',
        config: error?.config,
        __needLoginBlocked: true,
        __reportable: false,
      })
    }

    // 请求失败后统一交给 request monitor 分类，
    // 这里只保留原有的用户提示和 Promise 链路处理。
    reportRequestFailure(error)

    const response = error?.response
    const httpCode = response?.status
    console.log('请求错误', error)
    if (httpCode != 200) {
      const requestUrl = error?.config?.url || ''
      const errorMessage = `${$t('public.networkError')}${requestUrl ? ` (${requestUrl})` : ''}`
      ElMessage.error($t('public.networkError'))
      const nextError = new Error(errorMessage)
      nextError.__reportable = false
      nextError.config = error?.config
      nextError.response = response
      return Promise.reject(nextError)
    }

    return Promise.reject(error)
  }
)

export default instance
